import { lazy, Suspense, useEffect } from 'react'
import { Route, Routes } from 'react-router-dom'
import { TopPanel } from './components/TopPanel'
import { RightPanel } from './components/RightPanel'
import { MobileShell } from './components/MobileShell'
import { AddAccountModal } from './components/AddAccountModal'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ApiHealthBanner } from './components/ApiHealthBanner'
import { DeactivatedBanner } from './components/DeactivatedBanner'

// Route-level code splitting: pages load on first navigation instead of
// shipping in one monolithic chunk. Pages use named exports, so each lazy()
// maps the name onto `default`.
const Home = lazy(() => import('./pages/Home').then((m) => ({ default: m.Home })))
const Discovery = lazy(() => import('./pages/Discovery').then((m) => ({ default: m.Discovery })))
const Lurker = lazy(() => import('./pages/Lurker').then((m) => ({ default: m.Lurker })))
const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })))
const Profile = lazy(() => import('./pages/Profile').then((m) => ({ default: m.Profile })))
const FeedView = lazy(() => import('./pages/FeedView').then((m) => ({ default: m.FeedView })))
const ThreadView = lazy(() => import('./pages/ThreadView').then((m) => ({ default: m.ThreadView })))
const Calendar = lazy(() => import('./pages/Calendar').then((m) => ({ default: m.Calendar })))
const Schedule = lazy(() => import('./pages/Schedule').then((m) => ({ default: m.Schedule })))
const Posting = lazy(() => import('./pages/Posting').then((m) => ({ default: m.Posting })))
const AutoFollow = lazy(() => import('./pages/AutoFollow').then((m) => ({ default: m.AutoFollow })))
const AiCampaigns = lazy(() => import('./pages/AiCampaigns').then((m) => ({ default: m.AiCampaigns })))
const TabsSearch = lazy(() => import('./pages/TabsSearch').then((m) => ({ default: m.TabsSearch })))
const FailureLog = lazy(() => import('./pages/FailureLog').then((m) => ({ default: m.FailureLog })))
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })))
const Migrate = lazy(() => import('./pages/Migrate').then((m) => ({ default: m.Migrate })))
import { startScheduler } from './scheduler/runner'
import { hydrateAllAccounts } from './api/hydrateAccounts'
import { salvageCachedImages } from './api/salvageImages'
import { guardStorage } from './cache/db'
import { initAutoCompaction } from './cache/compactor'
import { initArchiver } from './archive/archiver'
import { RAW_CACHE } from './variant'
import { CompactionToast } from './components/CompactionToast'
import { useIsMobile } from './hooks/useViewport'

function AppRoutes() {
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="flex h-full min-h-[40vh] items-center justify-center">
            <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
          </div>
        }
      >
      <Routes>
        <Route path="/"              element={<Home />} />
        <Route path="/discovery"     element={<Discovery />} />
        <Route path="/lurker"        element={<Lurker />} />
        <Route path="/login"         element={<Login />} />
        <Route path="/profile/:handle" element={<Profile />} />
        <Route path="/post/:handle/:rkey" element={<ThreadView />} />
        <Route path="/feed/:encoded" element={<FeedView />} />
        <Route path="/calendar"      element={<Calendar />} />
        <Route path="/schedule"      element={<Schedule />} />
        <Route path="/posting"       element={<Posting />} />
        <Route path="/auto-follow"   element={<AutoFollow />} />
        <Route path="/ai-campaigns"  element={<AiCampaigns />} />
        <Route path="/tabs-search"   element={<TabsSearch />} />
        <Route path="/failure-log"   element={<FailureLog />} />
        <Route path="/settings"      element={<Settings />} />
        <Route path="/migrate"       element={<Migrate />} />
        <Route path="*"              element={<Home />} />
      </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}

export default function App() {
  const isMobile = useIsMobile()

  useEffect(() => {
    const stop = startScheduler()
    void hydrateAllAccounts()
    // Ask the browser for persistent storage so it never evicts the pinned
    // image cache or IndexedDB on its own — cached media must survive even
    // after Bluesky deletes the post upstream. Deliberately does NOT free
    // any cache: quota pressure is the compactor's job (recompression,
    // never eviction) — there is no deletion path in the app at all.
    void guardStorage()
    // Delayed so initial render + first hydrations aren't competing
    // with a full-cache image sweep. salvage itself is quota-aware.
    const salvage = setTimeout(() => void salvageCachedImages(), 8000)
    // Background cache compactor + disk archiver — fully disabled on the
    // RAW_CACHE variant (bskyhq.pages.dev), which keeps every image as an
    // unmanaged opaque cache block. See src/variant.ts.
    const stopCompactor = RAW_CACHE ? null : initAutoCompaction()
    const stopArchiver = RAW_CACHE ? null : initArchiver()
    return () => {
      stop()
      clearTimeout(salvage)
      stopCompactor?.()
      stopArchiver?.()
    }
  }, [])

  if (isMobile) {
    return (
      <>
        <MobileShell>
          <AppRoutes />
        </MobileShell>
        <AddAccountModal />
        <CompactionToast />
      </>
    )
  }

  return (
    <>
      <div className="flex h-screen w-screen flex-col overflow-hidden">
        <TopPanel />
        <DeactivatedBanner />
        <ApiHealthBanner />
        <div className="flex min-h-0 flex-1">
          <div className="surface-app min-h-0 min-w-0 flex-1 overflow-y-auto">
            <AppRoutes />
          </div>
          <RightPanel />
        </div>
      </div>
      <AddAccountModal />
      <CompactionToast />
    </>
  )
}
